import { useInfiniteQuery } from 'react-query';
import { FeaturedContent } from 'rwl-shared/dist/models';
import { useAPIRoute, useV2APIRoute } from '../../hooks/useAPIRoute';
import { fetchV2FeaturedContentPage } from '../v2/fetchV2FeaturedContent';

type UseFetchContentParams = Readonly<{
    accountId: string;
    enabled: boolean;
    postCode?: string;
    pageSize?: number;
}>;

type FetchError = {
    message: string;
    status: number;
};

type FetchContentParams = Readonly<{
    pageParam?: number;
    accountId: string;
    postCode?: string;
    url: string;
}>;

const fetchContent = async ({
    pageParam = 0,
    accountId,
    postCode,
    url,
}: FetchContentParams): Promise<FeaturedContent> => {
    try {
        const params = new URLSearchParams({
            accountId,
            postCodes: postCode || '',
            page: pageParam.toString(),
        });

        const result = await fetch(`${url}?${params}`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Cache-Control': 'no-cache', // Prevent browser caching since we use react-query
            },
        });

        if (!result.ok) {
            throw new Error(`HTTP error! status: ${result.status}`);
        }

        return result.json();
    } catch (error) {
        const errorMessage =
            error instanceof Error ? error.message : 'Unknown error occurred';
        throw {
            message: errorMessage,
            status: error instanceof Response ? error.status : 500,
        } as FetchError;
    }
};

const hasMoreContent = (lastPage: FeaturedContent) => {
    return Boolean(
        lastPage.jobs?.length ||
            lastPage.similarJobs?.length ||
            lastPage.reviews?.length ||
            lastPage.similarReviews?.length
    );
};

export const useFetchInfiniteContent = ({
    accountId,
    enabled,
    postCode,
    pageSize = 50,
}: UseFetchContentParams) => {
    const url = useAPIRoute('/serviceareas/featured', 'v1');
    const v2NeighborhoodUrl = useV2APIRoute('neighborhood');
    const v2ServiceAreaContentUrl = useV2APIRoute('serviceArea/content');
    // Both v2 URLs are required: page 1 embeds from neighborhood, deeper pages
    // come from serviceArea/content. An account with only one configured cannot
    // serve the full list, so treat it as v1.
    const useV2 = !!(v2NeighborhoodUrl && v2ServiceAreaContentUrl);

    return useInfiniteQuery<FeaturedContent, FetchError>(
        // v2 is part of the key so flipping the flag cannot serve a response the
        // other backend produced.
        [
            'useFetchInfiniteContent',
            accountId,
            postCode,
            pageSize,
            useV2 ? 'v2' : 'v1',
        ],
        async ({ pageParam = 0 }) => {
            if (useV2) {
                const v2Page = await fetchV2FeaturedContentPage({
                    neighborhoodUrl: v2NeighborhoodUrl,
                    serviceAreaContentUrl: v2ServiceAreaContentUrl,
                    accountId,
                    postCode,
                    pageParam,
                });
                // A defined result — even one with empty lists — is v2's answer.
                // Only an undefined result (v2 failed, already reported) falls
                // through to v1 for this page.
                if (v2Page) return v2Page;
            }
            return fetchContent({ accountId, postCode, url, pageParam });
        },
        {
            getNextPageParam: (
                lastPage: FeaturedContent,
                pages: FeaturedContent[]
            ) => (hasMoreContent(lastPage) ? pages.length : undefined),
            enabled,
            staleTime: 1000 * 60 * 60 * 24, // 24 hours
            cacheTime: 1000 * 10, // With map bounds, we'll rarely have a cache hit, but we don't want cache to bloat
            refetchOnWindowFocus: false, // Prevent unnecessary refetches
            retry: 2, // Retry failed requests twice
            retryDelay: (attemptIndex: number) =>
                Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
        }
    );
};
